import { useLazyQuery } from "@apollo/client";
import { compact, flatMap, uniq } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AssessmentQuestionResponses,
  AssessmentType,
  ComplianceProgramForReportAssessmentGroupSectionFragment,
  ComplianceProgramReportPrevAnswerFragment,
  GetAssessmentMostRecentAnswersQuery,
  GetAssessmentMostRecentAnswersQueryVariables,
  GetComplianceProgramForReportQuery,
  GetComplianceProgramForReportQueryVariables,
  GetPerformanceAssessmentChartLabelsQuery,
  GetPerformanceAssessmentChartLabelsQueryVariables,
  OverallGoalAssessmentQuestionNode,
  OverallRecognitionCoreValueAssessmentQuestionNode,
} from "types/graphql-schema";

import getAssessmentMostRecentAnswersQuery from "@apps/reporting/graphql/get-assessment-most-recent-answers-query";
import getComplianceProgramForReportQuery from "@apps/reporting/graphql/get-compliance-program-for-report-query";
import getPerformanceAssessmentChartLabelsQuery from "@apps/reporting/graphql/get-performance-assessment-chart-labels-query";
import { ComboboxGenericOption } from "@components/combobox/generic-combobox";
import { assertEdgesNonNull } from "@helpers/helpers";

import useReportingViews from "../reporting-views/use-reporting-views";
import {
  initAssessmentReportViewOptions,
  isAssessmentReportQuestionType,
} from "./helpers";
import { ChartLabel } from "./nine-box/types";
import { AssessmentReportViewOptions } from "./report-filters/types";

const useAssessmentReport = (
  assessmentType: AssessmentType,
  complianceProgramId: number,
) => {
  const [showViewOptionsSidebar, setShowViewOptionsSidebar] = useState(false);
  const [chartLabels, setChartLabels] = useState<ChartLabel[]>([]);
  const [baselineViewOptions, setBaselineViewOptions] =
    useState<AssessmentReportViewOptions>(initAssessmentReportViewOptions);
  const [viewOptions, setViewOptions] = useState<AssessmentReportViewOptions>(
    initAssessmentReportViewOptions,
  );

  const handleHideViewOptionsSidebar = useCallback(() => {
    setShowViewOptionsSidebar(false);
  }, []);
  const handleShowViewOptionsSidebar = useCallback(() => {
    setShowViewOptionsSidebar(true);
  }, []);
  const handleUpdateViewOptions = useCallback(
    (opts: Partial<AssessmentReportViewOptions>) => {
      setViewOptions((prev) => ({
        ...prev,
        isDirty: true,
        ...opts,
      }));
    },
    [],
  );
  const handleResetViewOptionsToBaseline = useCallback(() => {
    setViewOptions(baselineViewOptions);
  }, [baselineViewOptions]);
  const handleResetViewOptionsToEmpty = useCallback(() => {
    setBaselineViewOptions(initAssessmentReportViewOptions);
    setViewOptions(initAssessmentReportViewOptions);
  }, []);

  const [getReport, { data: programData, loading: isLoadingProgram }] =
    useLazyQuery<
      GetComplianceProgramForReportQuery,
      GetComplianceProgramForReportQueryVariables
    >(getComplianceProgramForReportQuery, {});
  const [getMostRecentAnswers, { data: mostRecentAnswersData }] = useLazyQuery<
    GetAssessmentMostRecentAnswersQuery,
    GetAssessmentMostRecentAnswersQueryVariables
  >(getAssessmentMostRecentAnswersQuery, {});

  const complianceProgram = useMemo(
    () => (programData ? programData?.complianceProgram : null),
    [programData],
  );

  const assessmentTemplate = useMemo(() => {
    if (!complianceProgram) {
      return null;
    }
    return assessmentType === AssessmentType.Performance
      ? complianceProgram.performanceAssessmentTemplate
      : complianceProgram.managerAssessmentTemplate;
  }, [assessmentType, complianceProgram]);

  const questions = useMemo(() => {
    if (!assessmentTemplate) {
      return [];
    }
    const overallGoalQuestion = assessmentTemplate.additionalQuestions.find(
      (q) => q && q.__typename === "OverallGoalAssessmentQuestionNode",
    ) as OverallGoalAssessmentQuestionNode | undefined;
    const overallValuesQuestion = assessmentTemplate.additionalQuestions.find(
      (q) =>
        q &&
        q.__typename === "OverallRecognitionCoreValueAssessmentQuestionNode",
    ) as OverallRecognitionCoreValueAssessmentQuestionNode | undefined;

    const questionSets = assertEdgesNonNull(assessmentTemplate.questionSets);
    const sections = questionSets.reduce((acc, set) => {
      return [...acc, ...assertEdgesNonNull(set.sections)];
    }, [] as ComplianceProgramForReportAssessmentGroupSectionFragment[]);
    const allQuestions = flatMap(sections, (section) =>
      assertEdgesNonNull(section.questions),
    );
    return compact([
      ...allQuestions.map((question) =>
        isAssessmentReportQuestionType(question.question) &&
        question.question.responses &&
        [
          AssessmentQuestionResponses.Both,
          AssessmentQuestionResponses.ExcludeSelfAssessment,
        ].includes(question.question.responses)
          ? question.question
          : null,
      ),
      overallGoalQuestion,
      overallValuesQuestion,
    ]);
  }, [assessmentTemplate]);
  const talentIndicatorQuestions = useMemo(() => {
    return questions.filter(
      (question) =>
        question.__typename === "TalentIndicatorAssessmentQuestionNode",
    );
  }, [questions]);

  const recentAnswerQuestionIds = useMemo(() => {
    const program = mostRecentAnswersData?.complianceProgram;

    const assessments = program
      ? compact(
          assertEdgesNonNull(program.performanceReportRows).map(
            (row) => row.assessment,
          ),
        )
      : [];
    return uniq([
      ...compact(
        assessments.flatMap((assessment) =>
          assessment.mostRecentAnswers?.map((ans) => ans?.questionId),
        ),
      ),
    ]);
  }, [mostRecentAnswersData]);

  const prevAnswersByAssessmentId: Record<
    number,
    ComplianceProgramReportPrevAnswerFragment[]
  > = useMemo(() => {
    const program = mostRecentAnswersData?.complianceProgram;
    return program?.performanceReportRows
      ? assertEdgesNonNull(program.performanceReportRows).reduce(
          (acc, row) => {
            const assessment = row.assessment;
            if (!assessment || !assessment.mostRecentAnswers) {
              return acc;
            }
            return {
              ...acc,
              [assessment.id]: compact(assessment.mostRecentAnswers),
            };
          },
          {} as Record<number, ComplianceProgramReportPrevAnswerFragment[]>,
        )
      : {};
  }, [mostRecentAnswersData]);

  const [getChartLabels] = useLazyQuery<
    GetPerformanceAssessmentChartLabelsQuery,
    GetPerformanceAssessmentChartLabelsQueryVariables
  >(getPerformanceAssessmentChartLabelsQuery, {
    variables: {
      questionIds: questions.map((q) => q.id),
    },
    onCompleted: (data) => {
      setChartLabels(
        data?.performanceAssessmentChartLabels
          ? assertEdgesNonNull(data.performanceAssessmentChartLabels)
          : [],
      );
    },
  });

  const {
    reportingViewsData,
    getReportingViews,
    handleSaveAsNewView,
    handleSaveExistingView,
    handleDuplicateView,
    handleDeleteView,
    isSavingReportingView,
    isDeletingReportingView,
    ReportingViewDeletionConfirm,
  } = useReportingViews({
    complianceProgramId,
    assessmentType,
    viewOptions,
    baselineViewOptions,
    onResetViewOptionsToBaseline: handleResetViewOptionsToBaseline,
    onUpdateViewOptions: handleUpdateViewOptions,
    onUpdateBaselineViewOptions: setBaselineViewOptions,
    onResetViewOptionsToEmpty: handleResetViewOptionsToEmpty,
  });

  useEffect(() => {
    handleResetViewOptionsToEmpty();
    getReport({
      variables: {
        complianceProgramId,
        isPerformanceAssessmentReport:
          assessmentType === AssessmentType.Performance,
        isUpwardAssessmentReport: assessmentType === AssessmentType.Manager,
      },
    });
    if (assessmentType === AssessmentType.Performance) {
      getMostRecentAnswers({
        variables: {
          complianceProgramId,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complianceProgramId]);
  useEffect(() => {
    if (complianceProgram) {
      getReportingViews({
        variables: {
          complianceProgramId: complianceProgram.id,
          assessmentType,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complianceProgram]);
  useEffect(() => {
    if (questions.length > 0) {
      getChartLabels({
        variables: {
          questionIds: questions.map((q) => q.id),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions]);

  const savedViewOptions = useMemo(() => {
    return reportingViewsData?.complianceProgramReportingViews
      ? assertEdgesNonNull(
          reportingViewsData.complianceProgramReportingViews,
        ).map((opt) => ({
          value: opt.id,
          label: opt.name ?? "",
        }))
      : [];
  }, [reportingViewsData]);
  const handleChangeSavedView = useCallback(
    (opt: ComboboxGenericOption<number>) => {
      if (!reportingViewsData?.complianceProgramReportingViews) {
        return;
      }
      const newViewOptionsData = assertEdgesNonNull(
        reportingViewsData.complianceProgramReportingViews,
      ).find((view) => view.id === opt.value);
      const newViewOptions = {
        id: newViewOptionsData?.id,
        name: newViewOptionsData?.name ?? "",
        isDirty: false,
        ...JSON.parse(newViewOptionsData?.viewOptions),
      };
      handleResetViewOptionsToBaseline();
      handleUpdateViewOptions(newViewOptions);
      setBaselineViewOptions(newViewOptions);
    },
    [
      handleResetViewOptionsToBaseline,
      handleUpdateViewOptions,
      reportingViewsData,
    ],
  );

  return {
    chartLabels,
    setChartLabels,
    viewOptions,
    showViewOptionsSidebar,
    handleHideViewOptionsSidebar,
    handleShowViewOptionsSidebar,
    handleUpdateViewOptions,
    handleResetViewOptionsToBaseline,
    handleSaveAsNewView,
    handleSaveExistingView,
    handleDuplicateView,
    handleDeleteView,
    complianceProgram,
    isLoadingProgram,
    assessmentTemplate,
    questions,
    talentIndicatorQuestions,
    isSavingReportingView,
    isDeletingReportingView,
    ReportingViewDeletionConfirm,
    savedViewOptions,
    handleChangeSavedView,
    recentAnswerQuestionIds,
    prevAnswersByAssessmentId,
  };
};

export default useAssessmentReport;
